import { get, post, postPublic, getRawPublic } from '../api.js';
import { hasKeys, getUserId, decryptWithLinkSecret, rewrapWithOwnKeys, rewrapFileKeyWithOwnKeys, rewrapItemKeyWithOwnKeys, decryptGrant, base64urlDecode, base64urlEncode, decryptClaimSecret, sha256hex, unwrapGrantItemKeyWithLinkSecret, decryptBlobWithKey, decryptFileBlobWithLinkSecret, unwrapLinkGrantKey, wipe } from '../crypto.js';
import { navigate } from '../router.js';
import { esc } from '../app.js';
import { buildPlainZip } from '../zip.js';
import { getToken, getGrantAcceptSecret, setGrantAcceptId, setGrantAcceptSecret, setGrantAcceptPersistent, clearGrantAccept, setPostAuthRedirect } from '../auth-store.js';

export async function renderGrantAccept(app, params) {
  const grantId = params.id;
  // Secret may come from URL params or sessionStorage (after replaceState strips it)
  const secret = params.secret || getGrantAcceptSecret();

  if (!grantId || !secret) {
    app.innerHTML = '<div class="msg msg-error">Invalid grant accept link</div>';
    return;
  }

  // Persist in sessionStorage so we survive auth redirects and replaceState
  setGrantAcceptId(grantId);
  setGrantAcceptSecret(secret);

  // Strip secret from URL to prevent history/referrer leakage
  // Do this AFTER persisting to sessionStorage
  if (params.secret && window.location.hash.includes(params.secret)) {
    history.replaceState(null, '', `#/grant-accept/${grantId}`);
  }

  const token = getToken();

  if (!token) {
    // Not authenticated — try unauthenticated preview first
    await renderPreview(app, grantId, secret);
    return;
  }

  if (!hasKeys()) {
    // Authenticated but no keys — redirect to setup-password or unlock
    setPostAuthRedirect(`#/grant-accept/${grantId}/${secret}`);
    try {
      const me = await get('/auth/me');
      // Passwordless users have public_key = [0] (placeholder)
      const needsSetup = me.public_key.length <= 1;
      if (needsSetup) {
        navigate('#/setup-password');
      } else {
        navigate('#/unlock');
      }
    } catch {
      navigate('#/unlock');
    }
    return;
  }

  // Authenticated with keys — process the grant (claim + rewrap)
  await processGrant(app, grantId, secret);
}

// ── Unauthenticated preview flow ──
async function renderPreview(app, grantId, secretStr) {
  app.innerHTML = '<p class="loading">Decrypting shared item...</p>';

  try {
    const claimKey = base64urlDecode(secretStr);
    const tokenHash = await sha256hex(claimKey);

    // Fetch encrypted grant data without authentication
    const grant = await postPublic(`/grants/${grantId}/preview`, { token_hash: tokenHash });

    // Decrypt the claim_ciphertext to get the link_secret
    const linkSecret = await decryptClaimSecret(claimKey, grant.claim_ciphertext);

    // Store for later use if user decides to add to vault
    setGrantAcceptSecret(base64urlEncode(linkSecret));

    // Decrypt the grant content
    const grantKeyBytes = new Uint8Array(grant.wrapped_key);
    const grantNonce = Array.from(grantKeyBytes.slice(0, 24));
    const grantCiphertext = Array.from(grantKeyBytes.slice(24));

    const plaintext = decryptWithLinkSecret(grantCiphertext, grantNonce, linkSecret, grant.encrypted_blob, undefined, grant.grantor_id);

    const policy = typeof grant.policy === 'string' ? JSON.parse(grant.policy) : grant.policy;
    const isGroupGrant = !!(grant.group_items && grant.group_id);

    if (isGroupGrant) {
      renderGroupPreview(app, grant, plaintext, policy, linkSecret, grantId, tokenHash);
    } else {
      renderSinglePreview(app, grant, plaintext, policy, linkSecret, grantId, tokenHash);
    }
  } catch (err) {
    // Preview failed — fall back to email form
    console.warn('Preview failed:', err);
    renderEmailForm(app, grantId);
  }
}

function renderSinglePreview(app, grant, plaintext, policy, linkSecret, grantId, tokenHash) {
  let displayContent = plaintext;
  let envelopeName = null;
  let isFile = false;
  let fileMimeType = 'application/octet-stream';
  try {
    const env = JSON.parse(plaintext);
    if (env && env.filename) {
      isFile = true;
      envelopeName = env.name || env.filename;
      fileMimeType = env.mime_type || 'application/octet-stream';
      displayContent = null;
    } else if (env && env.content !== undefined) {
      displayContent = env.content;
      envelopeName = env.name;
    }
  } catch { /* not an envelope */ }

  const hasFileKey = grant.file_wrapped_key && grant.file_wrapped_key.length > 0;
  const canDownload = policy?.allowed_ops?.includes('download');

  app.innerHTML = `
    <div class="card" style="max-width:600px;margin:40px auto">
      <div style="text-align:center;margin-bottom:20px">
        <div style="width:48px;height:48px;border-radius:50%;background:var(--accent);display:inline-flex;align-items:center;justify-content:center;margin-bottom:12px">
          <i data-lucide="lock-open" style="width:24px;height:24px;color:white"></i>
        </div>
        <h2 style="margin:0 0 4px">Shared with you</h2>
        <p style="color:var(--text-secondary);margin:0;font-size:14px">This item was shared securely via BlindKeep</p>
      </div>

      ${envelopeName ? `<div class="detail-row"><span class="detail-label">Name</span><span>${esc(envelopeName)}</span></div>` : ''}
      <div class="detail-row"><span class="detail-label">Type</span><span>${isFile ? 'Encrypted file' : 'Text'}</span></div>
      ${policy?.expires_at ? `<div class="detail-row"><span class="detail-label">Expires</span><span>${new Date(policy.expires_at).toLocaleString()}</span></div>` : ''}

      ${isFile ? `
        <div style="padding:16px;background:var(--bg-elevated);border-radius:8px;margin:16px 0">
          <div style="display:flex;align-items:center;gap:8px">
            <i data-lucide="file" style="width:20px;height:20px;color:var(--text-secondary)"></i>
            <div>
              <div style="font-weight:500">${esc(envelopeName)}</div>
              <div style="font-size:12px;color:var(--text-secondary)">${esc(fileMimeType)}</div>
            </div>
          </div>
        </div>
      ` : `
        <div style="margin:16px 0">
          <div style="font-size:12px;color:var(--text-secondary);text-transform:uppercase;letter-spacing:.5px;font-weight:500;margin-bottom:8px">Content</div>
          <div class="code-block" style="max-height:300px">${esc(displayContent)}</div>
        </div>
      `}

      <div style="display:flex;flex-direction:column;gap:8px;margin-top:20px">
        ${isFile && hasFileKey && canDownload ? `
          <button class="btn btn-primary" id="preview-download-btn" style="width:100%">
            <i data-lucide="download" style="width:16px;height:16px"></i> Download File
          </button>
        ` : ''}
        ${!isFile && displayContent ? `
          <button class="btn btn-primary" id="preview-copy-btn" style="width:100%">
            <i data-lucide="copy" style="width:16px;height:16px"></i> Copy to Clipboard
          </button>
        ` : ''}
        <button class="btn btn-secondary" id="preview-add-vault-btn" style="width:100%">
          <i data-lucide="plus" style="width:16px;height:16px"></i> Add to Vault
        </button>
        <p style="font-size:12px;color:var(--text-secondary);text-align:center;margin:4px 0 0">
          Create an account to save this item permanently and manage access
        </p>
      </div>
    </div>
  `;
  try { lucide.createIcons(); } catch (_) {}

  // Download file button
  const dlBtn = document.getElementById('preview-download-btn');
  if (dlBtn) {
    dlBtn.onclick = async () => {
      dlBtn.disabled = true;
      dlBtn.textContent = 'Downloading...';
      try {
        const ab = await getRawPublic(`/grants/${grantId}/preview-blob?token_hash=${tokenHash}`);
        const encryptedData = new Uint8Array(ab);
        const decrypted = decryptFileBlobWithLinkSecret(grant.file_wrapped_key, linkSecret, encryptedData, grant.grantor_id);
        const blob = new Blob([decrypted], { type: fileMimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = envelopeName || 'download';
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        dlBtn.textContent = `Failed: ${e.message}`;
      } finally {
        dlBtn.disabled = false;
        dlBtn.innerHTML = '<i data-lucide="download" style="width:16px;height:16px"></i> Download File';
        try { lucide.createIcons(); } catch (_) {}
      }
    };
  }

  // Copy text button
  const copyBtn = document.getElementById('preview-copy-btn');
  if (copyBtn) {
    copyBtn.onclick = () => {
      navigator.clipboard.writeText(displayContent);
      copyBtn.innerHTML = '<i data-lucide="check" style="width:16px;height:16px"></i> Copied!';
      try { lucide.createIcons(); } catch (_) {}
      setTimeout(() => {
        copyBtn.innerHTML = '<i data-lucide="copy" style="width:16px;height:16px"></i> Copy to Clipboard';
        try { lucide.createIcons(); } catch (_) {}
      }, 2000);
    };
  }

  // Add to vault button
  document.getElementById('preview-add-vault-btn').onclick = () => {
    renderEmailForm(app, grantId);
  };
}

function renderGroupPreview(app, grant, plaintext, policy, linkSecret, grantId, tokenHash) {
  let groupName = 'Collection';
  try {
    const env = JSON.parse(plaintext);
    if (env && env.name) groupName = env.name;
  } catch {}

  const wrappedItemKeys = grant.wrapped_item_keys || {};
  const itemDisplays = [];

  for (const item of grant.group_items) {
    const itemKeyData = wrappedItemKeys[item.id];
    let itemName = 'Untitled';
    let isFile = false;
    let fileMimeType = 'application/octet-stream';
    let textContent = null;

    if (itemKeyData) {
      let itemKey;
      try {
        itemKey = unwrapGrantItemKeyWithLinkSecret(itemKeyData.wrapped_key, itemKeyData.nonce, linkSecret);
        const decryptedText = decryptBlobWithKey(itemKey, item.encrypted_blob, grant.grantor_id);
        const env = JSON.parse(decryptedText);
        itemName = env.name || 'Untitled';
        if (env.filename) {
          isFile = true;
          fileMimeType = env.mime_type || 'application/octet-stream';
        } else if (env.content !== undefined) {
          textContent = env.content;
        }
      } catch (e) {
        console.warn('Failed to decrypt group item', item.id, e);
      } finally {
        wipe(itemKey);
      }
    }

    itemDisplays.push({ id: item.id, name: itemName, isFile, fileMimeType, textContent, hasFileKey: !!(itemKeyData && itemKeyData.file_wrapped_key) });
  }

  const canDownload = policy?.allowed_ops?.includes('download');
  const hasDownloadable = itemDisplays.some(i => (i.isFile && i.hasFileKey) || i.textContent !== null);

  app.innerHTML = `
    <div class="card" style="max-width:600px;margin:40px auto">
      <div style="text-align:center;margin-bottom:20px">
        <div style="width:48px;height:48px;border-radius:50%;background:var(--accent);display:inline-flex;align-items:center;justify-content:center;margin-bottom:12px">
          <i data-lucide="lock-open" style="width:24px;height:24px;color:white"></i>
        </div>
        <h2 style="margin:0 0 4px">${esc(groupName)}</h2>
        <p style="color:var(--text-secondary);margin:0;font-size:14px">Collection shared securely via BlindKeep</p>
      </div>

      <div class="detail-row"><span class="detail-label">Items</span><span>${itemDisplays.length}</span></div>
      ${policy?.expires_at ? `<div class="detail-row"><span class="detail-label">Expires</span><span>${new Date(policy.expires_at).toLocaleString()}</span></div>` : ''}

      <div style="margin:16px 0">
        ${itemDisplays.map(item => `
          <div class="grant-group-item" data-item-id="${item.id}">
            <div class="detail-row" style="align-items:center;cursor:pointer;border-bottom:none;padding:8px 0">
              <span style="display:flex;align-items:center;gap:6px">
                <i data-lucide="${item.isFile ? 'file' : 'lock'}" style="width:14px;height:14px"></i>
                <span style="font-weight:500">${esc(item.name)}</span>
                <span class="badge ${item.isFile ? 'badge-drop' : 'badge-encrypted'}" style="font-size:10px">${item.isFile ? 'File' : 'Text'}</span>
              </span>
              <span style="display:flex;align-items:center;gap:4px">
                ${item.isFile && item.hasFileKey && canDownload ? `<button class="btn btn-ghost btn-sm preview-dl-item-btn" data-item-id="${item.id}"><i data-lucide="download" style="width:14px;height:14px"></i></button>` : ''}
                <button class="btn btn-ghost btn-sm preview-view-item-btn" data-item-id="${item.id}"><i data-lucide="eye" style="width:14px;height:14px"></i></button>
              </span>
            </div>
            <div class="grant-item-detail" id="preview-item-detail-${item.id}" style="display:none;padding:0 0 12px;border-bottom:1px solid var(--border)"></div>
          </div>
        `).join('')}
      </div>

      <div style="display:flex;flex-direction:column;gap:8px;margin-top:20px">
        ${canDownload && hasDownloadable ? `
          <button class="btn btn-primary" id="preview-download-all-btn" style="width:100%">
            <i data-lucide="archive" style="width:16px;height:16px"></i> Download All as ZIP
          </button>
        ` : ''}
        <button class="btn btn-secondary" id="preview-add-vault-btn" style="width:100%">
          <i data-lucide="plus" style="width:16px;height:16px"></i> Add to Vault
        </button>
        <p style="font-size:12px;color:var(--text-secondary);text-align:center;margin:4px 0 0">
          Create an account to save this collection permanently and manage access
        </p>
      </div>
    </div>
  `;
  try { lucide.createIcons(); } catch (_) {}

  // Download All as ZIP
  const downloadAllBtn = document.getElementById('preview-download-all-btn');
  if (downloadAllBtn) {
    downloadAllBtn.onclick = async () => {
      downloadAllBtn.disabled = true;
      try {
        const downloadable = itemDisplays.filter(i => (i.isFile && i.hasFileKey) || i.textContent !== null);
        const zipFiles = [];

        for (let idx = 0; idx < downloadable.length; idx++) {
          downloadAllBtn.textContent = `Downloading ${idx + 1}/${downloadable.length}...`;
          const item = downloadable[idx];
          let fileData = null;
          let fileName = item.name || 'untitled';

          try {
            if (item.isFile && item.hasFileKey) {
              const ab = await getRawPublic(`/grants/${grantId}/preview-blob?token_hash=${tokenHash}&item_id=${item.id}`);
              const encryptedData = new Uint8Array(ab);
              const itemKeyData = wrappedItemKeys[item.id];
              const fwk = new Uint8Array(itemKeyData.file_wrapped_key);
              fileData = decryptFileBlobWithLinkSecret(fwk, linkSecret, encryptedData, grant.grantor_id);
            } else if (item.textContent !== null) {
              fileData = new TextEncoder().encode(item.textContent);
              if (!fileName.includes('.')) fileName += '.txt';
            }
          } catch (e) {
            fileData = new TextEncoder().encode(`Error downloading: ${e.message}`);
            fileName = (item.name || 'error') + '.error.txt';
          }

          if (fileData) {
            if (!(fileData instanceof Uint8Array)) fileData = new Uint8Array(fileData);
            const safeName = fileName.replace(/[/\\:*?"<>|]/g, '_').slice(0, 200) || 'unnamed';
            zipFiles.push({ name: safeName, data: fileData });
          }
        }

        downloadAllBtn.textContent = 'Building ZIP...';
        const zipData = await buildPlainZip(zipFiles);
        const blob = new Blob([zipData], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (groupName || 'collection') + '.zip';
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        downloadAllBtn.textContent = `Failed: ${e.message}`;
      } finally {
        downloadAllBtn.disabled = false;
        downloadAllBtn.innerHTML = '<i data-lucide="archive" style="width:16px;height:16px"></i> Download All as ZIP';
        try { lucide.createIcons(); } catch (_) {}
      }
    };
  }

  // Individual item view buttons
  app.querySelectorAll('.preview-view-item-btn').forEach(btn => {
    btn.onclick = (e) => {
      e.stopPropagation();
      const itemId = btn.dataset.itemId;
      const detailEl = document.getElementById(`preview-item-detail-${itemId}`);
      if (!detailEl) return;

      if (detailEl.style.display !== 'none') {
        detailEl.style.display = 'none';
        return;
      }

      const item = itemDisplays.find(i => i.id === itemId);
      if (!item) return;

      if (item.isFile) {
        detailEl.innerHTML = `
          <div style="padding:12px;background:var(--bg-elevated);border-radius:8px;margin-top:8px">
            <div style="font-size:13px;color:var(--text-secondary);margin-bottom:4px">Encrypted file</div>
            <div style="font-size:14px;font-weight:500">${esc(item.name)}</div>
            <div style="font-size:12px;color:var(--text-secondary);margin-top:2px">${esc(item.fileMimeType)}</div>
          </div>`;
      } else if (item.textContent !== null) {
        detailEl.innerHTML = `
          <div style="padding:12px;background:var(--bg-elevated);border-radius:8px;margin-top:8px">
            <div style="font-size:12px;color:var(--text-secondary);margin-bottom:8px;text-transform:uppercase;letter-spacing:.5px;font-weight:500">Content</div>
            <div class="code-block" style="max-height:200px">${esc(item.textContent)}</div>
          </div>`;
      } else {
        detailEl.innerHTML = `
          <div style="padding:12px;background:var(--bg-elevated);border-radius:8px;margin-top:8px">
            <div class="msg msg-error" style="font-size:12px;margin:0">Could not decrypt item content</div>
          </div>`;
      }

      detailEl.style.display = '';
      try { lucide.createIcons(); } catch (_) {}
    };
  });

  // Row click also toggles detail
  app.querySelectorAll('.grant-group-item .detail-row').forEach(row => {
    row.onclick = (e) => {
      if (e.target.closest('button')) return;
      const itemId = row.closest('.grant-group-item').dataset.itemId;
      const viewBtn = app.querySelector(`.preview-view-item-btn[data-item-id="${itemId}"]`);
      if (viewBtn) viewBtn.click();
    };
  });

  // Individual file download buttons
  app.querySelectorAll('.preview-dl-item-btn').forEach(btn => {
    btn.onclick = async (e) => {
      e.stopPropagation();
      const itemId = btn.dataset.itemId;
      btn.disabled = true;
      const origHtml = btn.innerHTML;
      btn.textContent = '...';
      try {
        const ab = await getRawPublic(`/grants/${grantId}/preview-blob?token_hash=${tokenHash}&item_id=${itemId}`);
        const encryptedData = new Uint8Array(ab);
        const itemKeyData = wrappedItemKeys[itemId];
        const fwk = new Uint8Array(itemKeyData.file_wrapped_key);
        const decrypted = decryptFileBlobWithLinkSecret(fwk, linkSecret, encryptedData, grant.grantor_id);
        const item = itemDisplays.find(i => i.id === itemId);
        const blob = new Blob([decrypted], { type: item?.fileMimeType || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = item?.name || 'download';
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Download failed:', e);
      } finally {
        btn.disabled = false;
        btn.innerHTML = origHtml;
        try { lucide.createIcons(); } catch (_) {}
      }
    };
  });

  // Add to vault button
  document.getElementById('preview-add-vault-btn').onclick = () => {
    renderEmailForm(app, grantId);
  };
}

// ── Email form for account creation (Add to vault flow) ──
function renderEmailForm(app, grantId) {
  app.innerHTML = `
    <div class="card" style="max-width:500px;margin:40px auto">
      <h1>Add to Vault</h1>
      <p style="color:var(--text-dim);margin-bottom:1rem">
        Create an account or sign in to save this item to your vault for permanent access.
      </p>
      <div id="accept-msg"></div>
      <form id="accept-email-form">
        <label>Email <input type="email" name="email" required></label>
        <button type="submit" class="btn btn-primary">Send Magic Link</button>
      </form>
    </div>
  `;

  document.getElementById('accept-email-form').onsubmit = async (e) => {
    e.preventDefault();
    const msg = document.getElementById('accept-msg');
    const btn = e.target.querySelector('button');
    const email = e.target.email.value;
    btn.disabled = true;
    msg.innerHTML = '';

    try {
      await post('/auth/magic-link', { email });
      // Persist to localStorage so the magic link (which opens in a new tab) can redirect back
      setGrantAcceptPersistent(grantId, getGrantAcceptSecret() || '');
      msg.innerHTML = `
        <div class="msg msg-success">
          Check your email for a magic link. After signing in, you'll be redirected back here to save the item to your vault.
        </div>
      `;
      btn.style.display = 'none';
      e.target.querySelector('input').disabled = true;
    } catch (err) {
      msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
      btn.disabled = false;
    }
  };
}

// ── Authenticated grant processing (claim + rewrap) ──
async function processGrant(app, grantId, secretStr) {
  app.innerHTML = '<p class="loading">Decrypting shared item...</p>';

  try {
    // Try claim-token flow first (new grants), fallback to legacy (URL = link_secret)
    let linkSecret;
    const claimKey = base64urlDecode(secretStr);
    try {
      const tokenHash = await sha256hex(claimKey);
      const resp = await post(`/grants/${grantId}/claim-token`, { token_hash: tokenHash });
      linkSecret = await decryptClaimSecret(claimKey, resp.claim_ciphertext);
    } catch (_) {
      // Legacy grant or already claimed — URL param IS the link_secret
      linkSecret = claimKey;
    }

    // Store decrypted link_secret so grant-access page can retry rewrap if needed
    setGrantAcceptSecret(base64urlEncode(linkSecret));

    // Access the grant — on failure, try to fetch grant metadata so we can
    // tailor the error screen when the viewer turns out to be the grantor
    // (who either came back to their own link or whose recipient has already
    // consumed a one-shot share). Non-grantors with a consumed link hit the
    // 410 branch; all other non-grantor errors rethrow to the generic handler.
    //
    // Note on the revoked-for-grantor branch: /access only returns 410 Gone
    // when the caller is the grantee. Grantors hit the `grantee_id/email`
    // check first and get 403 Forbidden regardless of status — the 403 never
    // reaches the 410 branch, so to tell "consumed" from "still active" for a
    // grantor we have to read grantMeta.status directly.
    let grant;
    try {
      grant = await post(`/grants/${grantId}/access`, { operation: 'view' });
    } catch (accessErr) {
      let grantMeta = null;
      try {
        grantMeta = await get(`/grants/${grantId}`);
      } catch (_) { /* fall through */ }
      const isGrantor = !!(grantMeta && grantMeta.grantor_id === getUserId());
      const accessGone = accessErr && accessErr.status === 410;
      const grantRevoked = accessGone || !!(grantMeta && grantMeta.status === 'revoked');

      if (isGrantor) {
        clearGrantAccept();
        const targetLabel = grantMeta.item_id ? 'item' : 'collection';
        const viewHref = grantMeta.item_id
          ? `#/items/${grantMeta.item_id}`
          : `#/items?view=collections&group=${grantMeta.group_id}`;
        const title = grantRevoked
          ? `Your share has been consumed`
          : `This is your shared ${targetLabel}`;
        const body = grantRevoked
          ? `The share link you created for <strong>${esc(grantMeta.grantee_email)}</strong>
             has been used (or its view limit has been reached) and can no longer
             be opened. You can still access your own copy of the ${targetLabel}.`
          : `You created this share link for <strong>${esc(grantMeta.grantee_email)}</strong>.
             It can only be accepted by the recipient.`;
        app.innerHTML = `
          <div class="card" style="max-width:500px;margin:60px auto;text-align:center">
            <h2 style="margin-bottom:12px">${title}</h2>
            <p style="color:var(--text-secondary);margin-bottom:20px">${body}</p>
            <div class="actions" style="justify-content:center">
              <a href="${viewHref}" class="btn btn-primary btn-sm" style="width:auto">View ${targetLabel}</a>
              <a href="#/grants" class="btn btn-secondary btn-sm" style="width:auto">All Grants</a>
            </div>
          </div>`;
        return;
      }

      if (accessGone) {
        clearGrantAccept();
        app.innerHTML = `
          <div class="card" style="max-width:500px;margin:60px auto;text-align:center">
            <h2 style="margin-bottom:12px">This link has already been used</h2>
            <p style="color:var(--text-secondary);margin-bottom:20px">
              The share was set to a single retrieval (or its view limit has been
              reached) and can no longer be opened. If you still need the contents,
              ask the sender to share it again.
            </p>
            <div class="actions" style="justify-content:center">
              <a href="#/" class="btn btn-primary btn-sm" style="width:auto">Back to home</a>
            </div>
          </div>`;
        return;
      }
      throw accessErr;
    }

    const grantKeyBytes = new Uint8Array(grant.wrapped_key);
    const grantNonce = Array.from(grantKeyBytes.slice(0, 24));
    const grantCiphertext = Array.from(grantKeyBytes.slice(24));

    let plaintext;
    if (grant.ephemeral_pubkey.length === 0) {
      // Link-secret grant — decrypt with link secret, then rewrap
      plaintext = decryptWithLinkSecret(grantCiphertext, grantNonce, linkSecret, grant.encrypted_blob, undefined, grant.grantor_id);

      // Re-wrap with own keys
      const rewrapped = rewrapWithOwnKeys(grantCiphertext, grantNonce, linkSecret);
      const newWrappedKey = [...rewrapped.nonce, ...rewrapped.wrapped_key];

      const hasFileKey = grant.file_wrapped_key && grant.file_wrapped_key.length > 0;
      const rewrapBody = {
        wrapped_key: newWrappedKey,
        ephemeral_pubkey: rewrapped.ephemeral_pubkey,
      };
      if (hasFileKey) {
        rewrapBody.file_wrapped_key = rewrapFileKeyWithOwnKeys(grant.file_wrapped_key, linkSecret);
      }
      // Rewrap per-item keys for group grants
      const oldItemKeys = grant.wrapped_item_keys;
      if (oldItemKeys && typeof oldItemKeys === 'object') {
        const newItemKeys = {};
        for (const [itemId, entry] of Object.entries(oldItemKeys)) {
          try {
            const rw = rewrapItemKeyWithOwnKeys(entry.wrapped_key, entry.nonce, linkSecret);
            const newEntry = { wrapped_key: [...rw.wrapped_key], nonce: [...rw.nonce], ephemeral_pubkey: [...rw.ephemeral_pubkey] };
            if (entry.file_wrapped_key) {
              newEntry.file_wrapped_key = rewrapFileKeyWithOwnKeys(entry.file_wrapped_key, linkSecret);
            }
            newItemKeys[itemId] = newEntry;
          } catch (_) {
            newItemKeys[itemId] = entry;
          }
        }
        rewrapBody.wrapped_item_keys = newItemKeys;
      }

      await post(`/grants/${grantId}/rewrap`, rewrapBody);
    } else {
      // Already rewrapped — use normal decryptGrant
      plaintext = decryptGrant(grant.wrapped_key, grant.ephemeral_pubkey, grant.encrypted_blob, grant.grantor_id);
    }

    // Clean up storage
    clearGrantAccept();

    // Redirect to the grant access page for the full UI
    navigate(`#/grants/${grantId}/access`);
  } catch (err) {
    app.innerHTML = `
      <div class="msg msg-error">${esc(err.message)}</div>
      <div class="actions"><a href="#/grants" class="btn btn-secondary">Go to Grants</a></div>
    `;
  }
}
